(() => {
  'use strict';

  const LEAD_LEVELS = ['fast', 'quick', 'standard', 'extended'];
  const LEAD_CLASSES = LEAD_LEVELS.map((level) => `vrs-lead-time--${level}`);
  const PICKUP_CLASSES = LEAD_LEVELS.map((level) => `vrs-pickup-status--${level}`);

  function safeJson(element) {
    if (!element) return [];

    try {
      return JSON.parse(element.textContent || '[]');
    } catch (error) {
      console.warn('VRS product stock data could not be read.', error);
      return [];
    }
  }

  function updateStockCard(card, record) {
    if (!card) return;

    if (!record) {
      card.hidden = true;
      return;
    }

    const count = Math.max(0, Number(record.inventory_quantity) || 0);
    const inventoryTracked = Boolean(record.inventory_management);
    const canSupplierOrder = record.inventory_policy === 'continue' || Boolean(record.available);
    const countElement = card.querySelector('[data-vrs-stock-count]');
    const labelElement = card.querySelector('.vrs-product-stock__label');
    const noteElement = card.querySelector('[data-vrs-stock-note]');
    const noteTitle = card.querySelector('[data-vrs-stock-note-title]');
    const noteText = card.querySelector('[data-vrs-stock-note-text]');

    card.hidden = false;
    card.classList.toggle('is-untracked', !inventoryTracked);
    card.classList.toggle('is-zero', inventoryTracked && count <= 0);
    card.classList.toggle('is-positive', inventoryTracked && count > 0);
    card.dataset.stockTracked = String(inventoryTracked);
    card.dataset.stockQuantity = inventoryTracked ? String(count) : '';
    card.dataset.stockPolicy = record.inventory_policy || '';
    card.dataset.variantAvailable = String(Boolean(record.available));

    if (labelElement) {
      labelElement.textContent = inventoryTracked
        ? (count <= 0 ? 'No stock available' : 'Stock available')
        : 'Stock status';
    }

    if (countElement) {
      countElement.textContent = inventoryTracked ? String(count) : 'Available to order';
    }

    if (noteElement) {
      const showNote = inventoryTracked && count <= 0;
      noteElement.hidden = !showNote;

      if (showNote && noteTitle && noteText) {
        if (canSupplierOrder) {
          noteTitle.textContent = 'Supplier order required.';
          noteText.textContent = ' This product will be ordered from the supplier and shipped as soon as it is received.';
        } else {
          noteTitle.textContent = 'Currently out of stock.';
          noteText.textContent = ' Contact VRSupply for the latest availability and ordering options.';
        }
      }
    }
  }

  function initStockCard(card) {
    if (!card || card.dataset.vrsStockReady === 'true') return;
    card.dataset.vrsStockReady = 'true';

    const inventoryElement = card.querySelector('product-inventory');
    const inventoryData = safeJson(
      inventoryElement?.querySelector('script[type="application/json"]')
    );

    updateStockCard(card, {
      inventory_quantity: inventoryElement?.dataset.inventoryQuantity || 0,
      available: inventoryElement?.dataset.variantAvailable === 'true',
      inventory_policy: inventoryElement?.dataset.inventoryPolicy || '',
      inventory_management: inventoryElement?.dataset.inventoryManagement || null
    });

    const pickupLink = card.querySelector('[data-vrs-scroll-pickup]');
    if (pickupLink) {
      pickupLink.addEventListener('click', () => {
        const productRoot = card.closest('.js-product') || document;
        const pickupAvailability = productRoot.querySelector('pickup-availability')
          || document.querySelector('pickup-availability');

        if (!pickupAvailability) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        pickupAvailability.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center'
        });
      });
    }

    const productInfo = card.closest('.product-info');
    const variantPicker = productInfo?.querySelector('variant-picker');
    if (!variantPicker) return;

    variantPicker.addEventListener('on:variant:change', (event) => {
      const selectedVariant = event.detail?.variant;

      if (!selectedVariant) {
        updateStockCard(card, null);
        return;
      }

      const record = inventoryData.find(
        (variant) => Number(variant.id) === Number(selectedVariant.id)
      );

      updateStockCard(card, record || {
        inventory_quantity: 0,
        available: selectedVariant.available,
        inventory_policy: selectedVariant.inventory_policy || '',
        inventory_management: selectedVariant.inventory_management || null
      });
    });
  }

  function leadTimeDetails(text) {
    const normalized = String(text || '').toLowerCase();
    if (!normalized.includes('usually ready')) return null;

    if (
      normalized.includes('hour')
      || normalized.includes('same day')
      || normalized.includes('today')
    ) {
      return { level: 'fast', label: 'Fast lead time', maxDays: 1 };
    }

    const numbers = (normalized.match(/\d+/g) || []).map(Number);
    if (!numbers.length) return null;

    const maxDays = Math.max(...numbers);

    if (maxDays <= 3) {
      return { level: 'fast', label: 'Fast lead time', maxDays };
    }

    if (maxDays <= 7) {
      return { level: 'quick', label: 'Quick lead time', maxDays };
    }

    if (maxDays <= 14) {
      return { level: 'standard', label: 'Standard lead time', maxDays };
    }

    return { level: 'extended', label: 'Extended lead time', maxDays };
  }

  function classifyPickupAvailability(pickupElement) {
    if (!pickupElement) return;

    pickupElement.querySelectorAll('p').forEach((paragraph) => {
      const details = leadTimeDetails(paragraph.textContent);
      if (!details) return;

      paragraph.classList.remove(...LEAD_CLASSES);
      paragraph.classList.add('vrs-lead-time', `vrs-lead-time--${details.level}`);
      paragraph.dataset.vrsLeadLabel = details.label;
      paragraph.dataset.vrsLeadMaxDays = String(details.maxDays);
      paragraph.setAttribute(
        'aria-label',
        `${details.label}. ${paragraph.textContent.trim()}`
      );

      const status = paragraph.closest('.pickup-status');
      if (status) {
        status.classList.remove(...PICKUP_CLASSES);
        status.classList.add('vrs-pickup-status', `vrs-pickup-status--${details.level}`);
      }
    });
  }

  function initPickupAvailability(pickupElement) {
    if (!pickupElement || pickupElement.dataset.vrsLeadReady === 'true') return;
    pickupElement.dataset.vrsLeadReady = 'true';

    classifyPickupAvailability(pickupElement);

    const observer = new MutationObserver(() => {
      classifyPickupAvailability(pickupElement);
    });

    observer.observe(pickupElement, { childList: true, subtree: true });
  }

  function measureBrandDescription(container) {
    const content = container.querySelector('[data-vrs-brand-description-content]');
    const button = container.querySelector('[data-vrs-brand-description-toggle]');
    if (!content || !button) return;

    if (container.classList.contains('is-expanded')) return;

    container.classList.remove('is-collapsible');
    button.hidden = true;

    requestAnimationFrame(() => {
      const collapsedHeight = 208;
      const shouldCollapse = content.scrollHeight > collapsedHeight + 12;
      container.classList.toggle('is-collapsible', shouldCollapse);
      button.hidden = !shouldCollapse;
    });
  }

  function initBrandDescription(container) {
    if (!container || container.dataset.vrsBrandReady === 'true') return;
    container.dataset.vrsBrandReady = 'true';

    const button = container.querySelector('[data-vrs-brand-description-toggle]');
    const content = container.querySelector('[data-vrs-brand-description-content]');
    if (!button || !content) return;

    button.addEventListener('click', () => {
      const expanded = container.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Show less' : 'See more';
    });

    measureBrandDescription(container);

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        measureBrandDescription(container);
      }, 160);
    }, { passive: true });
  }

  function productDescriptionBlockCount(content) {
    return Array.from(content.children).filter((child) => {
      if (!child.textContent?.trim()) return false;
      return ['P', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE', 'DIV'].includes(child.tagName);
    }).length;
  }

  function measureProductDescription(content) {
    const button = content.nextElementSibling?.matches?.('[data-vrs-product-description-toggle]')
      ? content.nextElementSibling
      : null;

    if (!button || content.classList.contains('is-expanded')) return;

    content.classList.remove('is-collapsible');
    button.hidden = true;

    requestAnimationFrame(() => {
      const hasMultipleBlocks = productDescriptionBlockCount(content) > 1;
      const exceedsPreview = content.scrollHeight > 188;
      const shouldCollapse = hasMultipleBlocks || exceedsPreview;

      content.classList.toggle('is-collapsible', shouldCollapse);
      button.hidden = !shouldCollapse;
    });
  }

  function initProductDescription(content) {
    if (!content || content.dataset.vrsDescriptionReady === 'true') return;
    if (content.closest('details')) return;

    content.dataset.vrsDescriptionReady = 'true';
    content.classList.add('vrs-product-description');

    if (!content.id) {
      content.id = `VrsProductDescription-${Math.random().toString(36).slice(2, 10)}`;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'vrs-product-description__toggle';
    button.dataset.vrsProductDescriptionToggle = '';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', content.id);
    button.textContent = 'See more';
    button.hidden = true;
    content.insertAdjacentElement('afterend', button);

    button.addEventListener('click', () => {
      const expanded = content.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? 'Show less' : 'See more';

      if (!expanded) {
        content.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'nearest'
        });
      }
    });

    content.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => measureProductDescription(content), { once: true });
      }
    });

    measureProductDescription(content);

    let resizeTimer;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => measureProductDescription(content), 160);
    }, { passive: true });
  }

  function initialize(root = document) {
    if (root.matches?.('[data-vrs-product-stock]')) initStockCard(root);
    if (root.matches?.('pickup-availability')) initPickupAvailability(root);
    if (root.matches?.('[data-vrs-brand-description]')) initBrandDescription(root);
    if (root.matches?.('.cc-main-product .product-info .product-description')) initProductDescription(root);

    root.querySelectorAll?.('[data-vrs-product-stock]').forEach(initStockCard);
    root.querySelectorAll?.('pickup-availability').forEach(initPickupAvailability);
    root.querySelectorAll?.('[data-vrs-brand-description]').forEach(initBrandDescription);
    root.querySelectorAll?.('.cc-main-product .product-info .product-description').forEach(initProductDescription);
  }

  function observeDynamicContent() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initialize(node);
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  observeDynamicContent();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });
})();
